import axios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';

export class HttpClient {
  private client: AxiosInstance;

  public defaultHeaders: Record<string, string>;

  constructor(baseURL: string, defaultHeaders?: Record<string, string>) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Cas-Shield-Site': window.location.origin
      }
    });
    this.defaultHeaders = defaultHeaders ?? {};
  }

  public get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get(url, config);
  }

  public post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post(url, data, config);
  }

  public patch<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch(url, data, config);
  }

  public put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put(url, data, config);
  }

  public delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete(url, config);
  }
}

export class RetryHttpClient extends HttpClient {
  private async retry<T>(
    fn: () => Promise<AxiosResponse<T>>,
    retriesLeft = 3,
    interval = 1000
  ): Promise<AxiosResponse<T>> {
    try {
      return await fn();
    } catch (error) {
      if (retriesLeft === 0) {
        return Promise.reject(error);
      }
      await new Promise(resolve => setTimeout(resolve, interval));
      return await this.retry(fn, retriesLeft - 1, interval);
    }
  }

  public getWithRetry<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.retry(() => this.get<T>(url, config));
  }

  public postWithRetry<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.retry(() => this.post<T>(url, data, config));
  }

  // other HTTP methods can be added here as needed
}
